import React, { useState, forwardRef, useCallback, useEffect } from 'react'
import { Select, Box } from '@sanity/ui'
import { set, unset } from 'sanity'
import { useClient } from 'sanity'
import { ObjectInputProps } from 'sanity'

const NumericReferenceInput = forwardRef<HTMLDivElement, ObjectInputProps>(
  (props, ref) => {
    const [documents, setDocuments] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    const { value, onChange, schemaType, readOnly } = props

    const client = useClient({ apiVersion: '2023-05-03' })

    // Get the document type from the reference definition
    const documentType = schemaType.to?.[0]?.type

    // Numeric sorting function
    const sortNumeric = useCallback((items: any[]) => {
      return items.sort((a, b) => {
        const aNum = parseFloat(a.name?.replace(/[^0-9.]/g, '') || '0')
        const bNum = parseFloat(b.name?.replace(/[^0-9.]/g, '') || '0')
        return aNum - bNum
      })
    }, [])

    // Handle change events
    const handleChange = useCallback(
      (newValue: any) => {
        onChange(newValue ? set(newValue) : unset())
      },
      [onChange],
    )

    // Handle dropdown selection
    const handleSelectChange = useCallback(
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedId = event.currentTarget.value
        if (!selectedId) {
          handleChange(null)
          return
        }

        handleChange({
          _ref: selectedId,
          _type: 'reference',
        })
      },
      [handleChange],
    )

    // Fetch documents
    useEffect(() => {
      let mounted = true

      if (!documentType) return
      const fetchDocuments = async () => {
        try {
          setLoading(true)
          const query = `*[
          _type == "${documentType.name}"
          && !(_id in path("drafts.**"))]{
            _id,
            name
          }`
          const docs = await client.fetch(query)
          const sortedDocs = sortNumeric(docs)
          if (mounted) {
            setDocuments(sortedDocs)
          }
        } catch (error) {
          if (mounted) {
            console.error('Error fetching documents:', error)
          }
        } finally {
          if (mounted) {
            setLoading(false)
          }
        }
      }

      fetchDocuments()

      return () => {
        mounted = false
      }
    }, [client, documentType, sortNumeric])

    // Title + description are rendered by Sanity's FormField wrapper around this custom
    // input — rendering them again here doubled the label and pushed the dropdown out of
    // alignment with sibling fields in 2-column fieldsets. Render only the Select.
    if (loading) {
      return (
        <Box ref={ref}>
          <Select disabled>
            <option>Loading...</option>
          </Select>
        </Box>
      )
    }

    return (
      <Box ref={ref}>
        <Select
          value={value?._ref || ''}
          onChange={handleSelectChange}
          disabled={readOnly}
        >
          <option value="">{`Select ${documentType?.name || 'item'}`}</option>
          {documents.map((doc) => (
            <option key={doc._id} value={doc._id}>
              {doc.name}
            </option>
          ))}
        </Select>
      </Box>
    )
  },
)

NumericReferenceInput.displayName = 'NumericReferenceInput'

export default NumericReferenceInput
